"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { useSession } from "@/lib/auth-react";
import { authFetch } from "@/utils/auth-fetch";
import { CREDITS_CHANGED_EVENT } from "@/utils/creditEvents";

interface ActiveCredit {
  expiryDate: string;
  amount: number;
}

interface CreditsResponse {
  balance: { totalHours: number; totalMinutes: number };
  active: ActiveCredit[];
}

const URGENT_DAYS = 3;
const WARNING_DAYS = 7;

function severityFor(credits: CreditsResponse | null):
  | "urgent"
  | "warning"
  | "neutral" {
  if (!credits) return "neutral";
  if (credits.balance.totalMinutes <= 0) return "urgent";
  const now = Date.now();
  const earliest = credits.active
    .map((c) => new Date(c.expiryDate).getTime())
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b)[0];
  if (!earliest) return "neutral";
  const daysUntil = (earliest - now) / (1000 * 60 * 60 * 24);
  if (daysUntil <= URGENT_DAYS) return "urgent";
  if (daysUntil <= WARNING_DAYS) return "warning";
  return "neutral";
}

function formatHours(totalMinutes: number): string {
  // Spell it out — a bare "0h" reads as "Oh" in the badge.
  if (totalMinutes <= 0) return "No credit";
  const hours = totalMinutes / 60;
  if (hours >= 10) return `${Math.round(hours)}h`;
  if (Number.isInteger(hours)) return `${hours}h`;
  return `${hours.toFixed(1)}h`;
}

export function StudentCreditBubble({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const { data: session } = useSession();
  const [credits, setCredits] = useState<CreditsResponse | null>(null);
  // Bump to force a re-fetch when a booking / purchase fires
  // `notifyCreditsChanged()`. The bubble is mounted in the navbar
  // (long-lived) so the post-action refresh has to be pushed to it.
  const [reloadTick, setReloadTick] = useState(0);

  const role = session?.user?.role;
  const accessToken = session?.accessToken;

  const handleCreditsChanged = useCallback(() => {
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (role !== "STUDENT") return;
    window.addEventListener(CREDITS_CHANGED_EVENT, handleCreditsChanged);
    return () => {
      window.removeEventListener(CREDITS_CHANGED_EVENT, handleCreditsChanged);
    };
  }, [role, handleCreditsChanged]);

  useEffect(() => {
    if (role !== "STUDENT" || !accessToken) return;
    let cancelled = false;
    authFetch("/api/credits", accessToken)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body?.data) return;
        setCredits(body.data as CreditsResponse);
      })
      .catch(() => {
        /* silent — bubble is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [role, accessToken, reloadTick]);

  if (session?.user?.role !== "STUDENT") return null;

  const severity = severityFor(credits);
  const label = credits ? formatHours(credits.balance.totalMinutes) : "…";
  const tooltip =
    severity === "urgent"
      ? credits && credits.balance.totalMinutes <= 0
        ? "You have no remaining lesson credits."
        : `A credit is expiring within ${URGENT_DAYS} days.`
      : severity === "warning"
        ? `A credit is expiring within ${WARNING_DAYS} days.`
        : "Your remaining lesson credits.";

  const styles =
    severity === "urgent"
      ? "bg-[#B53135] text-white ring-1 ring-white/30"
      : severity === "warning"
        ? "bg-[#F6C65A] text-[#27335D] ring-1 ring-white/30"
        : "bg-white/10 text-white ring-1 ring-white/20 hover:bg-white/15";

  const sizing =
    variant === "mobile"
      ? "h-7 min-w-[2.25rem] px-2 text-xs"
      : "h-8 min-w-[2.5rem] px-2.5 text-sm";

  return (
    <Link
      href="/dashboard/credits"
      data-testid="studentCreditBubble"
      data-severity={severity}
      title={tooltip}
      aria-label={
        credits && credits.balance.totalMinutes <= 0
          ? "No lesson credits remaining"
          : `${label} of lesson credits remaining`
      }
      className={`inline-flex items-center justify-center gap-1 rounded-full font-semibold tabular-nums transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 ${sizing} ${styles}`}
    >
      {label}
    </Link>
  );
}
